<template>
  <div class="b2b-schedule-page">
    <h1>B2B Account Schedule</h1>

    <div id="page-b2b-schedule" class="content-section">
    <div class="page-section">
      <div class="page-actions-bar">
        <div class="date-controls">
          <label for="schedule-date" class="date-label">Date</label>
          <input
            id="schedule-date"
            v-model="pickerDate"
            type="date"
            class="form-control date-input"
          >
          <button
            type="button"
            class="btn btn-secondary apply-btn"
            :disabled="loading || !canApply"
            @click="applyDate"
          >
            Apply
          </button>
        </div>

        <div v-if="!loading && accounts.length > 0" class="page-actions-summary">
          <span>Collections: {{ totalCollections }}</span>
          <span>Deliveries: {{ totalDeliveries }}</span>
          <span class="summary-indicator summary-indicator--cross" title="Missing collection or delivery">
            <font-awesome-icon icon="circle-xmark" />
            {{ statusCounts.cross }}
          </span>
          <span class="summary-indicator summary-indicator--warning" title="Status needs attention">
            <font-awesome-icon icon="triangle-exclamation" />
            {{ statusCounts.warning }}
          </span>
          <span class="summary-indicator summary-indicator--tick" title="On track">
            <font-awesome-icon icon="circle-check" />
            {{ statusCounts.tick }}
          </span>
        </div>

        <div class="page-actions-right">
          <button
            v-if="accounts.length > 0"
            type="button"
            class="btn btn-outline-secondary bar-action-btn"
            @click="collapseAll"
          >
            Collapse all
          </button>
          <button
            v-if="accounts.length > 0"
            type="button"
            class="btn btn-outline-secondary bar-action-btn"
            @click="expandAll"
          >
            Expand all
          </button>

          <router-link to="/b2b-schedule/admin" class="btn btn-primary admin-mode-btn">
            <font-awesome-icon icon="user-shield" class="me-2" />
            Admin Mode
          </router-link>
        </div>
      </div>

      <div class="schedule-scroll-body">
        <div v-if="loading" class="status-message">
          <font-awesome-icon icon="spinner" spin class="me-2" />
          Loading schedule…
        </div>

        <div v-else-if="errorMessage" class="status-message status-message--error">
          {{ errorMessage }}
        </div>

        <div v-else-if="accounts.length === 0" class="status-message">
          No accounts scheduled for this day.
        </div>

        <div v-else class="schedule-groups">
          <section
            v-for="group in groupedAccounts"
            :key="group.name"
            class="round-group-card"
          >
            <button
              type="button"
              class="group-header-btn"
              :style="getGroupHeaderStyle(group.name)"
              :aria-expanded="!isGroupCollapsed(group.name)"
              @click="toggleGroup(group.name)"
            >
              <div class="group-header-btn__main">
                <font-awesome-icon
                  :icon="isGroupCollapsed(group.name) ? 'chevron-right' : 'chevron-down'"
                  class="collapse-icon"
                />
                <span class="round-title" :title="group.name">{{ group.name }}</span>
              </div>

              <div
                class="group-header-btn__progress"
                :title="`Deliveries ready: ${group.deliveryProgress.ready} of ${group.deliveryProgress.total}`"
              >
                <div class="group-delivery-progress">
                  <div class="group-delivery-progress__track" aria-hidden="true">
                    <div
                      class="group-delivery-progress__fill"
                      :class="{ 'group-delivery-progress__fill--complete': group.deliveryProgress.percent === 100 }"
                      :style="{ width: `${group.deliveryProgress.percent}%` }"
                    />
                  </div>
                  <span class="group-delivery-progress__count">
                    {{ group.deliveryProgress.ready }}/{{ group.deliveryProgress.total }}
                  </span>
                </div>
              </div>

              <span class="account-count">
                {{ group.accounts.length }} account{{ group.accounts.length === 1 ? '' : 's' }}
              </span>
            </button>

            <div v-show="!isGroupCollapsed(group.name)" class="group-accounts">
              <div
                v-for="account in group.accounts"
                :key="account.customer_id"
                class="account-card"
              >
                <div
                  class="account-bar"
                  :class="{ 'account-bar--selectable': isSuperManager }"
                  role="button"
                  tabindex="0"
                  :aria-expanded="isExpanded(account.customer_id)"
                  @click="toggleExpanded(account.customer_id)"
                  @keydown.enter.prevent="toggleExpanded(account.customer_id)"
                  @keydown.space.prevent="toggleExpanded(account.customer_id)"
                >
                  <label v-if="isSuperManager" class="account-bar__checkbox" @click.stop>
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="isAccountSelected(account)"
                      :indeterminate="isAccountIndeterminate(account)"
                      @change="toggleAccountSelection(account, $event)"
                    >
                  </label>

                  <font-awesome-icon
                    :icon="isExpanded(account.customer_id) ? 'chevron-down' : 'chevron-right'"
                    class="account-bar__chevron"
                  />

                  <span class="account-bar__name-wrap">
                    <router-link
                      :to="{ name: 'ViewCustomer', params: { id: account.customer_id } }"
                      target="_blank"
                      class="account-bar__name"
                      @click.stop
                    >
                      {{ formatFullName(account.firstname, account.surname) }}
                    </router-link>
                  </span>

                  <div class="account-bar__days">
                    <div class="day-pills">
                      <span
                        v-for="day in dayOptions"
                        :key="`${account.customer_id}-${day.key}`"
                        class="day-pill day-pill--readonly"
                        :class="{ 'day-pill--active': Number(account.days?.[day.key]) === 1 }"
                      >
                        {{ day.label }}
                      </span>
                    </div>
                  </div>

                  <span class="account-bar__counts">
                    {{ formatOrderCountLabel(account.collection_count, account.delivery_count) }}
                  </span>

                  <span class="account-bar__status">
                    <font-awesome-icon
                      v-if="getAccountStatusIndicator(account) === 'cross'"
                      icon="circle-xmark"
                      class="account-bar__status-icon account-bar__status-icon--cross"
                      :title="getAccountStatusTitle('cross')"
                    />
                    <font-awesome-icon
                      v-else-if="getAccountStatusIndicator(account) === 'warning'"
                      icon="triangle-exclamation"
                      class="account-bar__status-icon account-bar__status-icon--warning"
                      :title="getAccountStatusTitle('warning')"
                    />
                    <font-awesome-icon
                      v-else-if="getAccountStatusIndicator(account) === 'tick'"
                      icon="circle-check"
                      class="account-bar__status-icon account-bar__status-icon--tick"
                      :title="getAccountStatusTitle('tick')"
                    />
                  </span>
                </div>

                <div v-if="isExpanded(account.customer_id)" class="account-orders">
                  <div
                    v-for="order in accountOrders(account)"
                    :key="orderKey(order)"
                    class="order-line-shell"
                    :class="{
                      'order-line-shell--warning': getAccountStatusIndicator(account) === 'warning'
                        && isOrderStatusWarning(order),
                    }"
                  >
                    <div
                      class="order-line"
                      :class="{ 'order-line--with-checkbox': isSuperManager }"
                    >
                      <label v-if="isSuperManager" class="order-line__checkbox-wrap" @click.stop>
                        <input
                          type="checkbox"
                          class="form-check-input"
                          :checked="isOrderSelected(order)"
                          @change="toggleOrderSelection(order, account, $event)"
                        >
                      </label>

                      <router-link
                        :to="{ name: 'ViewOrder', params: { id: order.order_id } }"
                        target="_blank"
                        class="order-line__number"
                        @click.stop
                      >
                        {{ order.order_id }}
                      </router-link>

                      <span class="order-line__type">
                        {{ formatOrderTypeLabel(order.type) }}
                      </span>

                      <span class="order-line__date-label">
                        {{ formatOrderDateLabel(order.type) }}
                      </span>
                      <span class="order-line__date-value">
                        {{ formatUKDate(order.date) }}
                      </span>

                      <span class="order-line__items-label">Total items:</span>
                      <span class="order-line__items-value">{{ order.item_count ?? 0 }}</span>

                      <span class="order-line__status">
                        {{ formatStatusLabel(order.status) }}
                        <span
                          v-if="order.on_manifest"
                          class="order-line__manifest-badge"
                          :title="formatManifestBadgeTitle(order)"
                        >M</span>
                      </span>
                    </div>
                  </div>

                  <p v-if="accountOrders(account).length === 0" class="order-line-shell order-line-shell--empty">
                    <span class="order-line order-line--empty">No orders for this date.</span>
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div v-if="isSuperManager && accounts.length > 0" class="schedule-footer">
        <div class="schedule-footer__actions">
          <div class="schedule-footer__selection">
            <button type="button" class="footer-link-btn" @click="selectAll">
              Select all
            </button>
            <span class="selection-divider">|</span>
            <button type="button" class="footer-link-btn" @click="deselectAll">
              Deselect all
            </button>
          </div>

          <button
            type="button"
            class="btn btn-primary manifest-btn"
            :disabled="manifestSubmitting"
            @click="openManifestModal"
          >
            Add to manifest
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>

  <div
    v-if="showManifestModal"
    class="modal d-block"
    tabindex="-1"
    role="dialog"
    @click.self="onManifestBackdropClick"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content manifest-modal">
        <div class="modal-header">
          <div>
            <p class="manifest-modal__eyebrow">Add to manifest</p>
            <h5 class="modal-title">
              {{ formatUKDate(appliedDate) }} — {{ selectedOrderCount }} order{{ selectedOrderCount === 1 ? '' : 's' }} selected
            </h5>
          </div>
          <button
            v-if="!manifestComplete && !manifestSubmitting"
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="closeManifestModal"
          />
        </div>

        <div class="modal-body">
          <div v-if="manifestComplete" class="manifest-modal__status manifest-modal__status--success">
            {{ manifestFeedback }}
          </div>

          <template v-else>
          <div v-if="selectedOrderCount === 0" class="manifest-modal__status">
            No orders selected
          </div>

          <div v-else-if="manifestLoading" class="manifest-modal__status">
            <font-awesome-icon icon="spinner" spin class="me-2" />
            Loading manifests…
          </div>

          <div v-else-if="manifestError" class="manifest-modal__status manifest-modal__status--error">
            {{ manifestError }}
          </div>

          <div v-else-if="manifestSets.length === 0" class="manifest-modal__status">
            No manifest sets found for this date.
          </div>

          <div v-else class="manifest-set-list">
            <label
              v-for="manifest in manifestSets"
              :key="manifest.manifest_set_id"
              class="manifest-set-option"
              :class="{ 'manifest-set-option--selected': selectedManifestSetId === manifest.manifest_set_id }"
            >
              <input
                v-model="selectedManifestSetId"
                type="radio"
                class="form-check-input"
                :value="manifest.manifest_set_id"
                :disabled="Number(manifest.is_closed) === 1"
              >
              <span class="manifest-set-option__content">
                <span class="manifest-set-option__title">
                  Set {{ manifest.manifest_set_id }}
                  <span v-if="Number(manifest.is_closed) === 1" class="manifest-set-option__badge">Frozen</span>
                </span>
                <span class="manifest-set-option__meta">
                  Created {{ manifest.date_label }}
                  · {{ manifest.rounds }} round{{ Number(manifest.rounds) === 1 ? '' : 's' }}
                  · {{ manifest.num_assigned }} order{{ Number(manifest.num_assigned) === 1 ? '' : 's' }} assigned
                </span>
              </span>
            </label>
          </div>

          <p v-if="manifestFeedback && !manifestComplete" class="manifest-modal__feedback" :class="{ 'manifest-modal__feedback--error': manifestFeedbackType === 'error' }">
            {{ manifestFeedback }}
          </p>
          </template>
        </div>

        <div class="modal-footer">
          <button
            v-if="manifestComplete"
            type="button"
            class="btn btn-primary"
            @click="closeManifestModal"
          >
            Close
          </button>
          <template v-else>
          <button type="button" class="btn btn-outline-secondary" :disabled="manifestSubmitting" @click="closeManifestModal">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!selectedManifestSetId || manifestLoading || manifestSubmitting || selectedOrderCount === 0 || selectedManifestSetIsFrozen"
            @click="confirmAddToManifest"
          >
            <font-awesome-icon v-if="manifestSubmitting" icon="spinner" spin class="me-2" />
            Confirm
          </button>
          </template>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showManifestModal" class="modal-backdrop show" />
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@/assets/css/b2b-page-fit.css';
import { useB2bPageFit } from '@/composables/useB2bPageFit';
import B2BScheduleService from '@/services/B2BScheduleService';
import ManifestService from '@/services/ManifestService';
import SessionService from '@/services/SessionService';
import {
  DAY_OPTIONS,
  countAccountStatusIndicators,
  formatFullName,
  formatManifestBadgeTitle,
  formatOrderCountLabel,
  formatOrderDateLabel,
  formatOrderTypeLabel,
  formatStatusLabel,
  formatUKDate,
  getAccountStatusIndicator,
  getAccountStatusTitle,
  getGroupDeliveryProgress,
  getRoundGroupTheme,
  groupAccounts,
  isOrderStatusWarning,
  toISODateString,
} from '@/utils/b2bScheduleHelpers';

const SUPER_MANAGER_LEVEL = 25;
const dayOptions = DAY_OPTIONS;

const pickerDate = ref(toISODateString());
const appliedDate = ref(toISODateString());
const accounts = ref([]);
const expandedByCustomerId = ref({});
const collapsedGroups = ref({});
const selectedOrderKeys = ref(new Set());
const selectedAccountIds = ref(new Set());
const loading = ref(false);
const errorMessage = ref('');
const isSuperManager = ref(false);

const showManifestModal = ref(false);
const manifestLoading = ref(false);
const manifestSubmitting = ref(false);
const manifestComplete = ref(false);
const manifestError = ref('');
const manifestFeedback = ref('');
const manifestFeedbackType = ref('success');
const manifestSets = ref([]);
const selectedManifestSetId = ref(null);

const canApply = computed(() => pickerDate.value !== appliedDate.value);
const groupedAccounts = computed(() => groupAccounts(accounts.value).map((group) => {
  const { ready, total } = getGroupDeliveryProgress(group.accounts);

  return {
    ...group,
    deliveryProgress: {
      ready,
      total,
      percent: total > 0 ? Math.round((ready / total) * 100) : 0,
    },
  };
}));
const totalCollections = computed(() => accounts.value.reduce(
  (sum, account) => sum + Number(account.collection_count || 0),
  0,
));
const totalDeliveries = computed(() => accounts.value.reduce(
  (sum, account) => sum + Number(account.delivery_count || 0),
  0,
));
const statusCounts = computed(() => countAccountStatusIndicators(accounts.value));
const selectedOrderCount = computed(() => selectedOrderKeys.value.size);
const selectedManifestSetIsFrozen = computed(() => {
  const selected = manifestSets.value.find(
    (manifest) => manifest.manifest_set_id === selectedManifestSetId.value,
  );

  return Number(selected?.is_closed) === 1;
});

function getSelectedOrders() {
  const selected = [];

  accounts.value.forEach((account) => {
    accountOrders(account).forEach((order) => {
      if (isOrderSelected(order)) {
        selected.push(order);
      }
    });
  });

  return selected;
}

function getGroupHeaderStyle(groupName) {
  const theme = getRoundGroupTheme(groupName);
  return {
    backgroundColor: theme.bg,
    borderBottom: `1px solid ${theme.border}`,
    color: theme.text,
  };
}

function isGroupCollapsed(groupName) {
  return Boolean(collapsedGroups.value[groupName]);
}

function toggleGroup(groupName) {
  collapsedGroups.value = {
    ...collapsedGroups.value,
    [groupName]: !isGroupCollapsed(groupName),
  };
}

function isExpanded(customerId) {
  return Boolean(expandedByCustomerId.value[customerId]);
}

function toggleExpanded(customerId) {
  expandedByCustomerId.value = {
    ...expandedByCustomerId.value,
    [customerId]: !isExpanded(customerId),
  };
}

function expandAll() {
  const expanded = {};
  accounts.value.forEach((account) => {
    expanded[account.customer_id] = true;
  });
  expandedByCustomerId.value = expanded;
  collapsedGroups.value = {};
}

function collapseAll() {
  expandedByCustomerId.value = {};
  const collapsed = {};
  groupedAccounts.value.forEach((group) => {
    collapsed[group.name] = true;
  });
  collapsedGroups.value = collapsed;
}

function accountOrders(account) {
  const collections = (account.collections || []).map((order) => ({
    ...order,
    type: 'collection',
  }));
  const deliveries = (account.deliveries || []).map((order) => ({
    ...order,
    type: 'delivery',
  }));

  return [...collections, ...deliveries];
}

function orderKey(order) {
  return `${order.type}-${order.order_id}`;
}

function isOrderSelected(order) {
  return selectedOrderKeys.value.has(orderKey(order));
}

function isAccountSelected(account) {
  const orders = accountOrders(account);

  if (orders.length === 0) {
    return selectedAccountIds.value.has(account.customer_id);
  }

  return orders.every((order) => isOrderSelected(order));
}

function isAccountIndeterminate(account) {
  const orders = accountOrders(account);

  if (orders.length === 0) {
    return false;
  }

  const selectedCount = orders.filter((order) => isOrderSelected(order)).length;
  return selectedCount > 0 && selectedCount < orders.length;
}

function toggleOrderSelection(order, account, event) {
  const key = orderKey(order);
  const next = new Set(selectedOrderKeys.value);

  if (event.target.checked) {
    next.add(key);
  } else {
    next.delete(key);
  }

  selectedOrderKeys.value = next;
  syncAccountSelection(account);
}

function toggleAccountSelection(account, event) {
  const orders = accountOrders(account);
  const nextOrders = new Set(selectedOrderKeys.value);
  const nextAccounts = new Set(selectedAccountIds.value);

  if (event.target.checked) {
    orders.forEach((order) => nextOrders.add(orderKey(order)));
    nextAccounts.add(account.customer_id);
  } else {
    orders.forEach((order) => nextOrders.delete(orderKey(order)));
    nextAccounts.delete(account.customer_id);
  }

  selectedOrderKeys.value = nextOrders;
  selectedAccountIds.value = nextAccounts;
}

function syncAccountSelection(account) {
  const orders = accountOrders(account);
  const nextAccounts = new Set(selectedAccountIds.value);

  if (orders.length > 0 && orders.every((order) => isOrderSelected(order))) {
    nextAccounts.add(account.customer_id);
  } else {
    nextAccounts.delete(account.customer_id);
  }

  selectedAccountIds.value = nextAccounts;
}

function selectAll() {
  const nextOrders = new Set();
  const nextAccounts = new Set();

  accounts.value.forEach((account) => {
    nextAccounts.add(account.customer_id);
    accountOrders(account).forEach((order) => nextOrders.add(orderKey(order)));
  });

  selectedOrderKeys.value = nextOrders;
  selectedAccountIds.value = nextAccounts;
}

function deselectAll() {
  selectedOrderKeys.value = new Set();
  selectedAccountIds.value = new Set();
}

function clearSelections() {
  deselectAll();
}

async function loadManifestSets() {
  manifestLoading.value = true;
  manifestError.value = '';

  try {
    const response = await ManifestService.getManifestSets(appliedDate.value);
    manifestSets.value = response.data?.data?.manifests || [];
    if (manifestSets.value.length > 0) {
      const openSet = manifestSets.value.find((manifest) => Number(manifest.is_closed) !== 1);
      selectedManifestSetId.value = openSet?.manifest_set_id ?? manifestSets.value[0].manifest_set_id;
    }
  } catch (error) {
    console.error(error);
    manifestError.value = error?.response?.data?.message || 'Failed to load manifests for this date.';
  } finally {
    manifestLoading.value = false;
  }
}

function onManifestBackdropClick() {
  if (manifestSubmitting.value || manifestComplete.value) {
    return;
  }

  closeManifestModal();
}

async function openManifestModal() {
  showManifestModal.value = true;
  manifestComplete.value = false;
  manifestFeedback.value = '';
  manifestFeedbackType.value = 'success';
  manifestSets.value = [];
  selectedManifestSetId.value = null;
  manifestError.value = '';

  if (selectedOrderCount.value > 0) {
    await loadManifestSets();
  }
}

watch(selectedOrderCount, (count) => {
  if (!showManifestModal.value || count === 0) {
    return;
  }

  if (manifestSets.value.length === 0 && !manifestLoading.value) {
    loadManifestSets();
  }
});

function closeManifestModal() {
  if (manifestSubmitting.value) {
    return;
  }

  const shouldRefresh = manifestComplete.value;

  showManifestModal.value = false;
  manifestComplete.value = false;
  manifestLoading.value = false;
  manifestError.value = '';
  manifestFeedback.value = '';
  manifestSets.value = [];
  selectedManifestSetId.value = null;

  if (shouldRefresh) {
    refreshScheduleData();
  }
}

async function refreshScheduleData() {
  try {
    const response = await B2BScheduleService.getDailySchedule(appliedDate.value);
    accounts.value = response.data?.data?.accounts || [];
    clearSelections();
  } catch (error) {
    console.error(error);
    errorMessage.value = error?.response?.data?.message || 'Failed to refresh B2B schedule.';
  }
}

async function confirmAddToManifest() {
  if (!selectedManifestSetId.value || selectedOrderCount.value === 0) {
    return;
  }

  const orders = getSelectedOrders().map((order) => ({
    order_id: order.order_id,
    type: order.type,
  }));

  manifestSubmitting.value = true;
  manifestFeedback.value = '';
  manifestFeedbackType.value = 'success';

  try {
    const response = await ManifestService.addOrdersToManifest(selectedManifestSetId.value, orders);
    const addedCount = Number(response.data?.data?.added_count || 0);
    const skippedCount = Number(response.data?.data?.skipped_count || 0);

    let message = `Added ${addedCount} order${addedCount === 1 ? '' : 's'} to manifest set ${selectedManifestSetId.value}.`;
    if (skippedCount > 0) {
      message += ` ${skippedCount} already on the manifest.`;
    }

    manifestFeedback.value = message;
    manifestFeedbackType.value = 'success';
    manifestComplete.value = true;
  } catch (error) {
    console.error(error);
    manifestFeedback.value = error?.response?.data?.message || 'Failed to add orders to manifest.';
    manifestFeedbackType.value = 'error';
  } finally {
    manifestSubmitting.value = false;
  }
}

async function loadUserAccess() {
  try {
    const response = await SessionService.getSessionDetails();
    const userLevel = Number(response.data?.data?.user?.userlevel || 0);
    isSuperManager.value = userLevel >= SUPER_MANAGER_LEVEL;
  } catch (error) {
    console.error(error);
    isSuperManager.value = false;
  }
}

async function loadSchedule(date) {
  loading.value = true;
  errorMessage.value = '';

  try {
    const response = await B2BScheduleService.getDailySchedule(date);
    accounts.value = response.data?.data?.accounts || [];
    expandedByCustomerId.value = {};
    collapsedGroups.value = {};
    clearSelections();
  } catch (error) {
    accounts.value = [];
    errorMessage.value = error?.response?.data?.message || 'Failed to load B2B schedule.';
    clearSelections();
  } finally {
    loading.value = false;
  }
}

function applyDate() {
  if (!pickerDate.value || pickerDate.value === appliedDate.value) {
    return;
  }

  appliedDate.value = pickerDate.value;
  loadSchedule(appliedDate.value);
}

useB2bPageFit();

onMounted(async () => {
  await Promise.all([
    loadUserAccess(),
    loadSchedule(appliedDate.value),
  ]);
});
</script>

<style scoped>
.page-actions-bar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.date-controls {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-self: start;
}

.page-actions-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.85rem 1.25rem;
  justify-self: center;
  font-size: 0.9rem;
  font-weight: 600;
  color: #17324d;
}

.summary-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.88rem;
}

.summary-indicator--cross {
  color: #c82333;
}

.summary-indicator--warning {
  color: #d97706;
}

.summary-indicator--tick {
  color: #198754;
}

.page-actions-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-self: end;
}

.bar-action-btn {
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  padding: 0.3rem 0.65rem;
}

.date-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #17324d;
}

.date-input {
  width: auto;
  min-width: 10.5rem;
  max-width: 12rem;
}

.apply-btn {
  font-weight: 600;
}

.admin-mode-btn {
  flex-shrink: 0;
  font-weight: 600;
  white-space: nowrap;
  justify-self: end;
}

.schedule-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  padding-top: 0.75rem;
  margin-top: 0.75rem;
  border-top: 1px solid rgba(15, 76, 129, 0.08);
}

.schedule-footer__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.schedule-footer__selection {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.footer-link-btn {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0f4c81;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
}

.footer-link-btn:hover {
  color: #1f7bb6;
  text-decoration: underline;
}

.selection-divider {
  color: #94a7b8;
  font-size: 0.85rem;
}

.manifest-btn {
  font-weight: 600;
  white-space: nowrap;
}

.manifest-btn:disabled {
  opacity: 0.65;
}

.manifest-modal {
  border: 0;
  border-radius: 14px;
  overflow: hidden;
}

.manifest-modal__eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #4b6478;
}

.manifest-modal__status {
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: #f5f9fc;
  color: #4b6478;
}

.manifest-modal__status--error {
  background: #f8d7da;
  color: #842029;
}

.manifest-modal__status--success {
  background: #d1e7dd;
  color: #0f5132;
}

.manifest-set-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.manifest-set-option {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(15, 76, 129, 0.12);
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.manifest-set-option:hover {
  border-color: rgba(31, 123, 182, 0.35);
}

.manifest-set-option--selected {
  border-color: #1f7bb6;
  box-shadow: 0 0 0 1px rgba(31, 123, 182, 0.15);
  background: #f5fbff;
}

.manifest-set-option__content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: left;
}

.manifest-set-option__title {
  font-weight: 700;
  color: #17324d;
}

.manifest-set-option__badge {
  margin-left: 0.45rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #fff3cd;
  color: #856404;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.manifest-set-option__meta {
  color: #4b6478;
  font-size: 0.88rem;
}

.manifest-modal__feedback {
  margin: 0.85rem 0 0;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  background: #e7f6ec;
  color: #1f5a38;
}

.manifest-modal__feedback--error {
  background: #f8d7da;
  color: #842029;
}

.status-message {
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(15, 76, 129, 0.08);
  color: #4b6478;
}

.status-message--error {
  color: #842029;
  background: #f8d7da;
  border-color: #f1aeb5;
}

.schedule-groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: start;
}

.round-group-card {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(15, 76, 129, 0.07);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(31, 123, 182, 0.05);
  overflow: hidden;
  min-width: 0;
}

.group-header-btn {
  display: grid;
  grid-template-columns: 7.25rem 23.75rem 1fr;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 0;
  cursor: pointer;
  text-align: left;
  transition: filter 0.15s ease;
}

.group-header-btn:hover {
  filter: brightness(0.98);
}

.group-header-btn__main {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 7.25rem;
  max-width: 7.25rem;
  overflow: hidden;
}

.group-header-btn__progress {
  display: flex;
  justify-content: flex-start;
  width: 23.75rem;
  max-width: 23.75rem;
  flex-shrink: 0;
}

.group-delivery-progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  flex-shrink: 0;
}

.group-delivery-progress__track {
  width: 16.25rem;
  flex-shrink: 0;
  height: 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.group-delivery-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.42;
  transition: width 0.25s ease;
}

.group-delivery-progress__fill--complete {
  opacity: 0.62;
}

.group-delivery-progress__count {
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  opacity: 0.92;
  white-space: nowrap;
}

.collapse-icon {
  width: 0.8rem;
}

.round-title {
  font-size: 0.95rem;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-count {
  font-size: 0.82rem;
  font-weight: 600;
  opacity: 0.9;
  white-space: nowrap;
  justify-self: end;
}

.group-accounts {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.45rem 0.55rem 0.55rem;
}

.account-card {
  border-radius: 8px;
  background: #fff;
  border: 1px solid rgba(15, 76, 129, 0.08);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.account-bar {
  width: 100%;
  display: grid;
  grid-template-columns: auto auto minmax(3.5rem, 0.8fr) minmax(6.75rem, 1fr) minmax(3.25rem, 0.7fr) 1rem;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.5rem;
  background: transparent;
  text-align: left;
  font-size: 0.84rem;
  color: #17324d;
  cursor: pointer;
}

.account-bar:hover {
  background: #f4f9fc;
}

.account-bar:focus-visible {
  outline: 2px solid rgba(31, 123, 182, 0.35);
  outline-offset: -2px;
}

.account-bar:not(.account-bar--selectable) {
  grid-template-columns: auto minmax(3.5rem, 0.8fr) minmax(6.75rem, 1fr) minmax(3.25rem, 0.7fr) 1rem;
}

.account-bar--selectable {
  grid-template-columns: auto auto minmax(3.5rem, 0.8fr) minmax(6.75rem, 1fr) minmax(3.25rem, 0.7fr) 1rem;
}

.account-bar__checkbox,
.order-line__checkbox-wrap {
  display: flex;
  align-items: center;
  margin: 0;
}

.account-bar__checkbox .form-check-input,
.order-line__checkbox-wrap .form-check-input {
  margin: 0;
  cursor: pointer;
}

.account-bar__checkbox .form-check-input:indeterminate {
  background-color: #f0c020;
  border-color: #c9a000;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%2317324d' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3e%3c/svg%3e");
}

.account-bar__chevron {
  width: 0.75rem;
  color: #4b6478;
  flex-shrink: 0;
}

.account-bar__name-wrap {
  justify-self: start;
  width: max-content;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}

.account-bar__name {
  display: inline;
  font-weight: 600;
  color: #0f4c81;
  text-decoration: none;
  pointer-events: auto;
}

.account-bar__name:hover {
  text-decoration: underline;
}

.account-bar__days {
  min-width: 0;
}

.account-bar__counts {
  color: #4b6478;
  font-size: 0.78rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-bar__status {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
}

.account-bar__status-icon--cross {
  color: #c82333;
  font-size: 0.95rem;
}

.account-bar__status-icon--warning {
  color: #d97706;
  font-size: 0.95rem;
}

.account-bar__status-icon--tick {
  color: #198754;
  font-size: 0.95rem;
}

.account-orders {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border-top: 1px solid rgba(15, 76, 129, 0.06);
  padding: 0.2rem 0.5rem 0.45rem calc(1.15rem - 5px);
}

.order-line-shell {
  position: relative;
  display: flex;
  width: calc(100% + 5px);
  min-width: 0;
  max-width: calc(100% + 5px);
  margin-left: -5px;
  align-self: stretch;
  box-sizing: border-box;
}

.order-line-shell--warning::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff8db;
  border-radius: 4px;
  z-index: 0;
  pointer-events: none;
}

.order-line {
  position: relative;
  z-index: 1;
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: 4.75rem 5.75rem 6.75rem 5.75rem 5.25rem 3.25rem minmax(7.75rem, 1fr);
  align-items: center;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0.2rem 0.35rem;
  font-size: 0.82rem;
  color: #17324d;
  column-gap: 0.65rem;
  box-sizing: border-box;
}

.order-line--with-checkbox {
  grid-template-columns: 1.1rem 4.75rem 5.75rem 6.75rem 5.75rem 5.25rem 3.25rem minmax(7.75rem, 1fr);
}

.order-line__checkbox-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  margin: 0;
}

.order-line--empty {
  display: block;
  margin: 0;
  color: #4b6478;
  font-style: italic;
}

.order-line-shell--empty {
  display: block;
  width: 100%;
  min-width: 0;
}

.order-line__number {
  font-weight: 600;
  color: #0f4c81;
  text-decoration: none;
}

.order-line__number:hover {
  text-decoration: underline;
}

.order-line__type {
  color: #4b6478;
}

.order-line__status {
  color: #4b6478;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.35rem;
  min-width: 0;
}

.order-line__manifest-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 1.35rem;
  padding: 0 0.2rem;
  border-radius: 0.2rem;
  background: #1f5f8b;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.order-line__date-label,
.order-line__items-label {
  color: #4b6478;
  text-align: right;
  white-space: nowrap;
}

.order-line__date-value,
.order-line__items-value {
  color: #4b6478;
  text-align: left;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .schedule-groups {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .account-bar,
  .account-bar--selectable {
    grid-template-columns: auto 1fr auto;
  }

  .account-bar__name-wrap {
    grid-column: 2;
  }

  .account-bar__days {
    grid-column: 1 / -1;
    padding-left: 3.2rem;
  }

  .account-bar--selectable .account-bar__days {
    padding-left: 3.2rem;
  }

  .account-bar:not(.account-bar--selectable) .account-bar__days {
    padding-left: 1.4rem;
  }

  .account-bar__counts {
    grid-column: 2;
  }

  .account-bar__status {
    grid-column: 3;
    justify-self: end;
  }

  .order-line,
  .order-line--with-checkbox {
    grid-template-columns: 1fr;
    gap: 0.15rem;
  }

  .order-line__status {
    text-align: left;
  }

  .order-line__date-label,
  .order-line__items-label {
    text-align: left;
  }
}

@media (max-width: 768px) {
  .page-actions-bar {
    grid-template-columns: 1fr;
    justify-items: stretch;
  }

  .page-actions-summary {
    justify-self: center;
  }

  .page-actions-right {
    justify-self: end;
    flex-wrap: wrap;
  }

  .admin-mode-btn {
    justify-self: end;
  }

  .schedule-footer {
    flex-wrap: wrap;
    gap: 0.65rem;
  }

  .schedule-footer__actions {
    flex-wrap: wrap;
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
